import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BottomSheet } from "wiz-front-shared/components/bottomSheet/BottomSheet";
import { Button } from "wiz-front-shared/components/button/Button";
import './WheelPicker.css';

const ITEM_HEIGHT = 40;
const VISIBLE_ITEMS = 5;

interface WheelColumnProps {
    items: string[];
    value: string;
    onChange: (value: string) => void;
    isOpen?: boolean;
}

const WheelColumn = ({ items, value, onChange, isOpen }: WheelColumnProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [translateY, setTranslateY] = useState(0);
    const isDragging = useRef(false);
    const isAnimating = useRef(false);
    const startY = useRef(0);
    const startTranslateY = useRef(0);
    const velocity = useRef(0);
    const lastY = useRef(0);
    const lastTime = useRef(0);
    const animationRef = useRef<number | undefined>(undefined);

    // 선택된 값의 인덱스로 translateY 계산
    const getTranslateYFromIndex = useCallback((index: number) => {
        return -index * ITEM_HEIGHT;
    }, []);

    // translateY로부터 인덱스 계산
    const getIndexFromTranslateY = useCallback((y: number) => {
        const index = Math.round(-y / ITEM_HEIGHT);
        return Math.max(0, Math.min(items.length - 1, index));
    }, [items.length]);

    // 초기값 설정 (드래그/애니메이션 중이 아닐 때만)
    useEffect(() => {
        if (isDragging.current || isAnimating.current) return;
        const index = items.indexOf(value);
        if (index !== -1) {
            setTranslateY(getTranslateYFromIndex(index));
        }
    }, [value, items, getTranslateYFromIndex]);

    // 닫힐 때 애니메이션 정리
    useEffect(() => {
        if (!isOpen) {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = undefined;
            }
            isDragging.current = false;
            isAnimating.current = false;
            velocity.current = 0;
        }
    }, [isOpen]);

    // 스냅 애니메이션
    const snapToItem = useCallback((targetY: number) => {
        const index = getIndexFromTranslateY(targetY);
        const snapY = getTranslateYFromIndex(index);

        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        isAnimating.current = true;

        // 애니메이션 시작 전에 즉시 값 업데이트 (확인 버튼 누를 때 값이 반영되도록)
        onChange(items[index]);

        const animate = () => {
            setTranslateY(prev => {
                const diff = snapY - prev;
                if (Math.abs(diff) < 1) {
                    isAnimating.current = false;
                    return snapY;
                }
                animationRef.current = requestAnimationFrame(animate);
                return prev + diff * 0.2;
            });
        };
        animate();
    }, [getIndexFromTranslateY, getTranslateYFromIndex, items, onChange]);

    // 관성 스크롤
    const momentumScroll = useCallback(() => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        isAnimating.current = true;
        let currentY = translateY;
        let lastIndex = getIndexFromTranslateY(translateY);

        const animate = () => {
            velocity.current *= 0.95; // 감속

            if (Math.abs(velocity.current) < 0.5) {
                snapToItem(currentY);
                return;
            }

            const newY = currentY + velocity.current;
            const minY = getTranslateYFromIndex(items.length - 1);
            const maxY = 0;

            // 경계 처리
            if (newY > maxY) {
                velocity.current *= 0.5;
                currentY = maxY;
            } else if (newY < minY) {
                velocity.current *= 0.5;
                currentY = minY;
            } else {
                currentY = newY;
            }

            // 인덱스가 변경되었을 때 즉시 값 업데이트
            const currentIndex = getIndexFromTranslateY(currentY);
            if (currentIndex !== lastIndex) {
                onChange(items[currentIndex]);
                lastIndex = currentIndex;
            }

            setTranslateY(currentY);
            animationRef.current = requestAnimationFrame(animate);
        };
        animate();
    }, [getIndexFromTranslateY, getTranslateYFromIndex, items, snapToItem, translateY, onChange]);

    const handleTouchStart = (e: React.TouchEvent) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        isDragging.current = true;
        startY.current = e.touches[0].clientY;
        startTranslateY.current = translateY;
        lastY.current = e.touches[0].clientY;
        lastTime.current = Date.now();
        velocity.current = 0;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current) return;

        const currentY = e.touches[0].clientY;
        const diff = currentY - startY.current;
        const newTranslateY = startTranslateY.current + diff;

        // 속도 계산
        const now = Date.now();
        const dt = now - lastTime.current;
        if (dt > 0) {
            velocity.current = (currentY - lastY.current) / dt * 16;
        }
        lastY.current = currentY;
        lastTime.current = now;

        // 경계에서 저항감
        const minY = getTranslateYFromIndex(items.length - 1);
        const maxY = 0;

        if (newTranslateY > maxY) {
            setTranslateY(maxY + (newTranslateY - maxY) * 0.3);
        } else if (newTranslateY < minY) {
            setTranslateY(minY + (newTranslateY - minY) * 0.3);
        } else {
            setTranslateY(newTranslateY);
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;

        if (Math.abs(velocity.current) > 1) {
            momentumScroll();
        } else {
            snapToItem(translateY);
        }
    };

    // 마우스 이벤트 (데스크톱 지원)
    const handleMouseDown = (e: React.MouseEvent) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        isDragging.current = true;
        startY.current = e.clientY;
        startTranslateY.current = translateY;
        lastY.current = e.clientY;
        lastTime.current = Date.now();
        velocity.current = 0;

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging.current) return;

            const currentY = e.clientY;
            const diff = currentY - startY.current;
            const newTranslateY = startTranslateY.current + diff;

            const now = Date.now();
            const dt = now - lastTime.current;
            if (dt > 0) {
                velocity.current = (currentY - lastY.current) / dt * 16;
            }
            lastY.current = currentY;
            lastTime.current = now;

            const minY = getTranslateYFromIndex(items.length - 1);
            const maxY = 0;

            if (newTranslateY > maxY) {
                setTranslateY(maxY + (newTranslateY - maxY) * 0.3);
            } else if (newTranslateY < minY) {
                setTranslateY(minY + (newTranslateY - minY) * 0.3);
            } else {
                setTranslateY(newTranslateY);
            }
        };

        const handleMouseUp = () => {
            isDragging.current = false;

            if (Math.abs(velocity.current) > 1) {
                momentumScroll();
            } else {
                snapToItem(translateY);
            }

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // 클릭으로 아이템 선택
    const handleItemClick = (index: number) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        const targetY = getTranslateYFromIndex(index);
        snapToItem(targetY);
    };

    return (
        <div
            className="wheel-column"
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
        >
            <div
                className="wheel-column-items"
                style={{
                    transform: `translateY(${translateY + ITEM_HEIGHT * Math.floor(VISIBLE_ITEMS / 2)}px)`
                }}
            >
                {items.map((item, index) => {
                    const currentIndex = getIndexFromTranslateY(translateY);
                    const distance = Math.abs(index - currentIndex);
                    const opacity = Math.max(0.3, 1 - distance * 0.25);
                    const scale = Math.max(0.85, 1 - distance * 0.05);

                    return (
                        <div
                            key={index}
                            className={`wheel-column-item ${index === currentIndex ? 'selected' : ''}`}
                            style={{
                                height: ITEM_HEIGHT,
                                opacity,
                                transform: `scale(${scale})`,
                            }}
                            onClick={() => handleItemClick(index)}
                        >
                            {item}
                        </div>
                    );
                })}
            </div>
            <div className="wheel-column-highlight" style={{ height: ITEM_HEIGHT }} />
        </div>
    );
};

// DateWheelPicker
interface DateWheelPickerProps {
    value: string; // "YYYY-MM-DD" 형식
    onChange: (value: string) => void;
    minYear?: number;
    maxYear?: number;
}

export interface DateWheelPickerRef {
    open: () => void;
    close: () => void;
}

export const DateWheelPicker = forwardRef<DateWheelPickerRef, DateWheelPickerProps>(
    ({ value, onChange, minYear = 1900, maxYear = new Date().getFullYear() }, ref) => {
        const { t } = useTranslation();
        const [isOpen, setIsOpen] = useState(false);

        // 현재 선택된 값 파싱
        const parseDate = (dateStr: string) => {
            if (!dateStr) {
                const today = new Date();
                return {
                    year: String(today.getFullYear()),
                    month: String(today.getMonth() + 1).padStart(2, '0'),
                    day: String(today.getDate()).padStart(2, '0'),
                };
            }
            const [year, month, day] = dateStr.split('-');
            return { year, month, day };
        };

        const [tempDate, setTempDate] = useState(parseDate(value));

        // 년도 목록 (역순)
        const years = Array.from(
            { length: maxYear - minYear + 1 },
            (_, i) => String(maxYear - i)
        );

        // 월 목록
        const months = Array.from(
            { length: 12 },
            (_, i) => String(i + 1).padStart(2, '0')
        );

        // 일 목록 (선택된 년/월에 따라 동적)
        const getDaysInMonth = (year: string, month: string) => {
            const y = parseInt(year);
            const m = parseInt(month);
            return new Date(y, m, 0).getDate();
        };

        const days = Array.from(
            { length: getDaysInMonth(tempDate.year, tempDate.month) },
            (_, i) => String(i + 1).padStart(2, '0')
        );

        // 월이 바뀌면 일 조정
        useEffect(() => {
            const maxDay = getDaysInMonth(tempDate.year, tempDate.month);
            if (parseInt(tempDate.day) > maxDay) {
                setTempDate(prev => ({ ...prev, day: String(maxDay).padStart(2, '0') }));
            }
        }, [tempDate.year, tempDate.month, tempDate.day]);

        // 열릴 때 현재 값으로 초기화
        useEffect(() => {
            if (isOpen) {
                setTempDate(parseDate(value));
            }
        }, [isOpen, value]);

        useImperativeHandle(ref, () => ({
            open: () => setIsOpen(true),
            close: () => setIsOpen(false),
        }));

        const handleConfirm = () => {
            const dateStr = `${tempDate.year}-${tempDate.month}-${tempDate.day}`;
            onChange(dateStr);
            setIsOpen(false);
        };

        return (
            <BottomSheet isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="date-wheel-picker">
                    <div className="date-wheel-picker-wheels">
                        <WheelColumn
                            items={years}
                            value={tempDate.year}
                            onChange={(year) => setTempDate(prev => ({ ...prev, year }))}
                            isOpen={isOpen}
                        />
                        <WheelColumn
                            items={months}
                            value={tempDate.month}
                            onChange={(month) => setTempDate(prev => ({ ...prev, month }))}
                            isOpen={isOpen}
                        />
                        <WheelColumn
                            items={days}
                            value={tempDate.day}
                            onChange={(day) => setTempDate(prev => ({ ...prev, day }))}
                            isOpen={isOpen}
                        />
                    </div>
                    <div className="date-wheel-picker-btn-container">
                        <Button
                            buttonColor="var(--fixedBlack)"
                            text={t("common.cancel")}
                            onPress={() => setIsOpen(false)}
                        />
                        <Button
                            text={t("common.confirm")}
                            onPress={handleConfirm}
                        />
                    </div>
                </div>
            </BottomSheet>
        );
    }
);
